import React, { useRef, useState } from 'react';
import { ui } from './ui';
import QuestionCard from './QuestionCard';
import { blankQuestion, blankSection, cloneSection, newId, sectionTotal, TYPES, TYPE_META } from './paperUtils';

// Section & pattern builder. `sections` is controlled via setSections(updater).
export default function SectionsPanel({ sections, setSections, canAI, onRegen, busyId }) {
  const drag = useRef(null); // { kind: 'q'|'s', id }
  const [addType, setAddType] = useState({});

  const patchSection = (sid, patch) => setSections((ss) => ss.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const patchQ = (sid, qid, patch) => setSections((ss) => ss.map((s) => (s.id !== sid ? s
    : { ...s, questions: s.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)) })));
  const moveSection = (i, d) => setSections((ss) => {
    const j = i + d; if (j < 0 || j >= ss.length) return ss;
    const c = [...ss]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });

  // Move a question (from any section) to before `beforeQid` in section `sid` (or to the end).
  const moveQuestion = (qid, sid, beforeQid) => setSections((ss) => {
    let moved = null;
    const stripped = ss.map((s) => ({ ...s, questions: s.questions.filter((q) => { if (q.id === qid) { moved = q; return false; } return true; }) }));
    if (!moved || qid === beforeQid) return ss;
    return stripped.map((s) => {
      if (s.id !== sid) return s;
      const idx = beforeQid ? s.questions.findIndex((q) => q.id === beforeQid) : -1;
      const qs = [...s.questions];
      qs.splice(idx < 0 ? qs.length : idx, 0, moved);
      return { ...s, questions: qs };
    });
  });

  const addQuestion = (sid) => {
    const type = addType[sid] || 'short';
    const marks = { mcq: 1, fill_blank: 1, true_false: 1, very_short: 1, short: 3, long: 5, match: 4, case_study: 4 }[type] || 1;
    setSections((ss) => ss.map((s) => (s.id === sid ? { ...s, questions: [...s.questions, blankQuestion(type, marks)] } : s)));
  };
  const dupQuestion = (sid, q) => setSections((ss) => ss.map((s) => {
    if (s.id !== sid) return s;
    const i = s.questions.findIndex((x) => x.id === q.id);
    const qs = [...s.questions]; qs.splice(i + 1, 0, { ...q, id: newId(), options: [...(q.options || [])] });
    return { ...s, questions: qs };
  }));

  let counter = 0;
  return (
    <div>
      {sections.map((s, si) => (
        <div
          key={s.id}
          style={{ ...ui.card, borderLeft: '4px solid #4f46e5' }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { if (drag.current) { moveQuestion(drag.current, s.id, null); drag.current = null; } }}
        >
          <div style={ui.row}>
            <input style={{ ...ui.input, flex: 1, fontWeight: 700 }} value={s.sectionTitle} placeholder="Section title" onChange={(e) => patchSection(s.id, { sectionTitle: e.target.value })} />
            <b style={{ fontSize: 12 }}>{sectionTotal(s)} marks</b>
            <button type="button" style={ui.btn} onClick={() => moveSection(si, -1)} disabled={si === 0} title="Move up">↑</button>
            <button type="button" style={ui.btn} onClick={() => moveSection(si, 1)} disabled={si === sections.length - 1} title="Move down">↓</button>
            <button type="button" style={ui.btn} onClick={() => setSections((ss) => { const c = [...ss]; c.splice(si + 1, 0, cloneSection(s)); return c; })}>Clone</button>
            <button type="button" style={ui.btnDanger} onClick={() => { if (!s.questions.length || window.confirm(`Delete ${s.sectionTitle} and its ${s.questions.length} question(s)?`)) setSections((ss) => ss.filter((x) => x.id !== s.id)); }}>Delete</button>
          </div>
          <input style={{ ...ui.input, marginTop: 6 }} value={s.sectionInstructions} placeholder="Sub-instructions, e.g. Each question carries 1 mark" onChange={(e) => patchSection(s.id, { sectionInstructions: e.target.value })} />
          <div style={{ marginTop: 8 }}>
            {s.questions.map((q) => {
              const n = counter++;
              return (
                <QuestionCard
                  key={q.id} q={q} index={n}
                  onChange={(patch) => patchQ(s.id, q.id, patch)}
                  onDelete={() => setSections((ss) => ss.map((x) => (x.id === s.id ? { ...x, questions: x.questions.filter((y) => y.id !== q.id) } : x)))}
                  onDuplicate={() => dupQuestion(s.id, q)}
                  onRegen={canAI ? (mod) => onRegen(s.id, q, mod) : null}
                  busy={busyId === q.id}
                  onDragStart={(id) => { drag.current = id; }}
                  onDropOn={(beforeId) => { if (drag.current) moveQuestion(drag.current, s.id, beforeId); drag.current = null; }}
                />
              );
            })}
            {!s.questions.length && <div style={{ fontSize: 12, color: '#94a3b8', padding: 8 }}>No questions yet — add one below, use AI, or paste text. You can also drag questions here.</div>}
          </div>
          <div style={ui.row}>
            <select style={{ ...ui.input, width: 'auto' }} value={addType[s.id] || 'short'} onChange={(e) => setAddType({ ...addType, [s.id]: e.target.value })}>
              {TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t]}</option>)}
            </select>
            <button type="button" style={ui.btn} onClick={() => addQuestion(s.id)}>+ Add question</button>
          </div>
        </div>
      ))}
      <button type="button" style={ui.btnPrimary} onClick={() => setSections((ss) => [...ss, blankSection(ss.length)])}>+ Add Section</button>
    </div>
  );
}
